import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Button, { BUTTON_TYPES } from '../atoms/Button';
import css from './AlertMessage.css';
import Icon, { IconSizes, IconTypes, IconVariants } from '../foundations/Icon';

const ICON_VARIANT_MAP = {
    success: IconVariants.SUCCESS,
    error: IconVariants.ERROR,
    warning: IconVariants.WARNING,
    info: IconVariants.LIGHT,
};

const ICON_NAME_MAP = {
    success: 'circle-check',
    error: 'circle-x',
    warning: 'alert-triangle',
    info: 'info-circle',
};

export const LAYOUT_OPTIONS = {
    SNACKBAR: 'snackbar',
    WARNING: 'warning',
    EMBEDDED: 'embedded',
};

const AlertMessage = ({
    level,
    layout,
    title,
    text,
    className,
    buttonType,
    ctaText,
    onClick,
    isClosable,
    textReplace = {},
    titleTextReplace = {},
    onClose,
    shouldShow = true,
}) => {
    const handleClose = () => {
        onClose && onClose();
    };

    const hasButton = buttonType && ctaText && onClick;

    const titleOrFallback = layout === 'snackbar' ? title || level : title;

    return (
        shouldShow && (
            <div
                className={classNames(
                    css.message,
                    css[`level-${level}`],
                    css[`layout-${layout}`],
                    { [css.withButton]: hasButton },
                    className
                )}
            >
                <div className={css.alertContainer}>
                    <div className={css.alertIconContainer}>
                        <Icon
                            name={ICON_NAME_MAP[level]}
                            size={IconSizes.SMALL}
                            variant={ICON_VARIANT_MAP[level]}
                            className={css.alertIcon}
                        />
                    </div>
                    <div className={css.textContainer}>
                        <span className={css.title}>
                            <Translate id={titleOrFallback} data={titleTextReplace} />
                        </span>
                        <span className={css.text}>
                            <Translate id={text} data={textReplace} />
                        </span>
                    </div>
                    {hasButton && (
                        <div className={css.buttonContainer}>
                            <Button type={buttonType} onClick={onClick}>
                                {ctaText}
                            </Button>
                        </div>
                    )}
                </div>
                {isClosable && (
                    <Icon type={IconTypes.MINGCUTE} name="close-line" size={IconSizes.SMALL} onClick={handleClose} />
                )}
            </div>
        )
    );
};

AlertMessage.propTypes = {
    buttonType: PropTypes.oneOf([null, ...Object.values(BUTTON_TYPES)]),
    ctaText: PropTypes.string,
    onClick: PropTypes.func,
    onClose: PropTypes.func,
    level: PropTypes.oneOf(['success', 'error', 'warning', 'info']).isRequired,
    layout: PropTypes.oneOf(Object.values(LAYOUT_OPTIONS)).isRequired,
    title: PropTypes.string,
    text: PropTypes.string.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    textReplace: PropTypes.objectOf(PropTypes.any),
    // eslint-disable-next-line react/forbid-prop-types
    titleTextReplace: PropTypes.objectOf(PropTypes.any),
    className: PropTypes.string,
    isClosable: PropTypes.bool,
    shouldShow: PropTypes.bool,
};

export default AlertMessage;
